import { Drawer, FormControl, FormControlLabel, FormGroup, Grid, InputAdornment, MenuItem, Paper, Switch, TextField } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { SelectValidator, ValidatorForm } from "react-material-ui-form-validator";
import { GardaVettingDurationValidityEnum } from "../../_common/enum/gardaVettingDurationValidity.enum";
import { LicenseDurationValidityEnum } from "../../_common/enum/licenseDurationValidity.enum";
import { IOfficialLicenseGradeListData } from "../../_common/interface/officialLicenseGrade/iOfficialLicenseGradeListData";
import { IFilteringViewModel } from "../../_common/interface/table";
import { GenericAutoComplete } from "../../_components/autocomplete/generic.autocomplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import StyledGrid from "../../_components/grid/styledGrid";
import { PageHeader } from "../../_components/header/page.header";
import { CommonTable } from "../../_components/table/common.table";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { splitWordOnCamelCase } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { officialLicenseGradeService } from "../../_services/officialLicenseGrade.service";
import { officialLicenseTypeService } from "../../_services/officialLicenseType.service";
import { useContainerStyles } from "../../_styles/container.styles";
import { useDrawerStyles } from "../../_styles/drawer.styles";

function OfficialLicenseGradeManageDrawer(params: { isOpen: boolean, itemId: number | null, onSave(): void, onClose(): void }) {

    const resetData = () => ({
        id: 0,
        officialLicenseTypeId: 0,
        name: "",
        price: 0.00,
        hasGardaVetting: true,
        licenseDurationValidityId: "",
        gardaVettingDurationValidityId: "",
    });

    const [data, setData] = useState<any>(resetData);

    useEffect(() => {
        if (!params.itemId) {
            setData(resetData());
            return;
        }
        fetchData();
    }, [params.itemId]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function fetchData() {
        if (!params.itemId) return;

        setIsLoading(true)
        return officialLicenseGradeService
            .get(params.itemId)
            .then(
                (json) => {
                    if (json && json.hasGardaVetting === undefined)
                        json.hasGardaVetting = true;
                    if (json && (json.gardaVettingDurationValidityId === undefined || json.gardaVettingDurationValidityId === null))
                        json.gardaVettingDurationValidityId = GardaVettingDurationValidityEnum.FiscalYear;
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return officialLicenseGradeService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.onSave();
                },
                (e) => toastr.error(e)
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!params.itemId) return;
        setIsLoading(true);
        return officialLicenseGradeService
            .disable(params.itemId)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                },
                (e) => {
                    toastr.error(e);
                }
            )
            .finally(() => setIsLoading(false));
    }

    function handleDrawerChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }))
    };


    return (

        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: true
            }}
        >

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!params.itemId) ? "Update Official Licence" : "Create Official Licence"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="name"
                                    name="name"
                                    label="Name"
                                    required
                                    fullWidth
                                    validators={['required', 'minStringLength:1']}
                                    errorMessages={['required field', "Field cannot be empty"]}
                                    value={data.name}
                                    onChange={handleDrawerChange}
                                    autoFocus
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <GenericAutoComplete
                                    key="officialLicenseTypeId"
                                    label="Official Licence Type"
                                    hasParent={false}
                                    itemId={data.officialLicenseTypeId}
                                    name={"officialLicenseTypeId"}
                                    onItemChange={handleDrawerChange}
                                    isRequired={true}
                                    itemsLoader={officialLicenseTypeService.dropdown}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    type="number"
                                    key="price"
                                    name="price"
                                    label="Price"
                                    required
                                    fullWidth
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    InputProps={{
                                        startAdornment: data.price ? <InputAdornment position="start">{"\u20AC"}</InputAdornment> : null,
                                    }}
                                    value={data.price}
                                    onChange={handleDrawerChange}
                                    InputLabelProps={{
                                        shrink: !data.price && data.price !== 0,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <SelectValidator
                                        label="Duration Validity"
                                        name="licenseDurationValidityId"
                                        value={data.licenseDurationValidityId}
                                        placeholder="Duration Validity"
                                        validators={['required']}
                                        fullWidth
                                        errorMessages={['required field']}
                                        required
                                        onChange={handleDrawerChange}
                                        InputLabelProps={{
                                            shrink: !!data.licenseDurationValidityId,
                                        }}
                                        variant="outlined">
                                        <MenuItem value={""}>Select</MenuItem>
                                        <MenuItem value={LicenseDurationValidityEnum.FiscalYear}>Fiscal Year</MenuItem>
                                        <MenuItem value={LicenseDurationValidityEnum.CalendarYear}>Calendar Year</MenuItem>
                                    </SelectValidator>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!data.hasGardaVetting}
                                                onChange={() => { setData({ ...data, hasGardaVetting: !data.hasGardaVetting }); }}
                                            />
                                        }
                                        label="Has Garda vetting"
                                    />
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <SelectValidator
                                        label="Garda vetting duration"
                                        name="gardaVettingDurationValidityId"
                                        value={data.gardaVettingDurationValidityId}
                                        placeholder="Garda vetting duration"
                                        validators={['required']}
                                        fullWidth
                                        errorMessages={['required field']}
                                        required
                                        onChange={handleDrawerChange}
                                        InputLabelProps={{
                                            shrink: !!data.gardaVettingDurationValidityId,
                                        }}
                                        variant="outlined">
                                        <MenuItem value={""}>Select</MenuItem>
                                        <MenuItem value={GardaVettingDurationValidityEnum.FiscalYear}>Fiscal Year</MenuItem>
                                        <MenuItem value={GardaVettingDurationValidityEnum.CalendarYear}>Calendar Year</MenuItem>
                                    </SelectValidator>
                                </FormControl>
                            </Grid>

                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id === 0 ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Save"}
                                </PrimaryButton>
                            </Grid>

                            {data.id > 0 &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="block"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer>
    );
}

export function OfficialLicenseGradeIndex() {

    const [data, setData] = useState<IOfficialLicenseGradeListData>({ items: [], count: 0 });
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState<IFilteringViewModel>({
        name: '',
    });

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'price',
                header: 'Price',
            },
            {
                accessorKey: 'durationValidityName',
                header: 'Duration Validity',
                accessorFn: (rowData: any) => <>{splitWordOnCamelCase(rowData.durationValidityName)}</>,
            },
            {
                accessorKey: 'officialLicenseTypeName',
                header: 'Type',
            },
            {
                accessorKey: 'hasGardaVetting',
                header: 'Garda vetting',
                accessorFn: (rowData: any) => (rowData.hasGardaVetting ? 'Yes' : 'No'),
            },
            {
                accessorKey: 'gardaVettingDurationValidityName',
                header: 'Garda vetting duration',
                accessorFn: (rowData: any) => <>{splitWordOnCamelCase(rowData.gardaVettingDurationValidityName)}</>,
            },
        ],
        [],
    );

    useEffect(() => {
        fetchData();
    }, [filter]);

    function fetchData() {

        return officialLicenseGradeService.getAll({ ...filter })
            .then(
                (payload) => {
                    setData(payload);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any) {
        setFilter((__filter: any) => { return { ...__filter, [event.target.name]: event.target.value } });
    };


    return (
        <>
            <div style={useContainerStyles.root}>

                <PageHeader
                    pageName="Official Licences"
                    buttonName="Official Licence"
                    onClick={() => setSelectedId(0)}
                    actionType="create"
                />

                <Paper>

                    <ValidatorForm onSubmit={fetchData}>

                        <StyledGrid container spacing={2}>

                            <Grid item lg={3} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField label="Name" name="name" value={filter.name} onChange={handleChange} placeholder="Name" variant="outlined" />
                                </FormControl>
                            </Grid>

                        </StyledGrid>

                    </ValidatorForm>

                    <CommonTable
                        columns={columns}
                        data={data}
                        filter={filter}
                        onEdit={setSelectedId}
                        setFilter={(_filter) => { setFilter({ ...filter, ..._filter }) }}
                        isLoading={isLoading}
                    />

                    <OfficialLicenseGradeManageDrawer
                        isOpen={selectedId != null}
                        itemId={selectedId}
                        onSave={() => { setSelectedId(null); fetchData(); }}
                        onClose={() => { setSelectedId(null); fetchData(); }}
                    />

                </Paper>

            </div>
        </>

    );

}
